import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card } from '../../components/ui/card';
import { Home, Mail, UserX } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminNewsletter() {
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSubscribers();
  }, [token, navigate, filter]);

  const fetchSubscribers = async () => {
    try {
      setLoading(true);
      const activeParam = filter === 'active' ? 'true' : filter === 'inactive' ? 'false' : '';
      const response = await axios.get(`${API_URL}/api/newsletter/subscribers?active=${activeParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(response.data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnsubscribe = async (id) => {
    if (!window.confirm('Are you sure you want to unsubscribe this email?')) return;

    try {
      await axios.delete(`${API_URL}/api/newsletter/unsubscribe/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscriber unsubscribed successfully');
      fetchSubscribers();
    } catch (error) {
      console.error('Error unsubscribing:', error);
      toast.error('Failed to unsubscribe');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportEmails = () => {
    const emails = subscribers.map(s => s.email).join('\n');
    const blob = new Blob([emails], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
    toast.success('Emails exported successfully');
  };

  return (
    <>
      <Helmet>
        <title>Newsletter Subscribers · Admin</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur">
          <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <h1 className="font-display text-2xl font-bold">Newsletter</h1>
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/admin">
                    <Home className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="font-display text-3xl font-bold mb-2">Newsletter Subscribers</h2>
              <p className="text-muted-foreground">
                {subscribers.length} {filter === 'active' ? 'active' : filter === 'inactive' ? 'inactive' : 'total'} subscribers
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={exportEmails} variant="outline" size="sm" disabled={subscribers.length === 0}>
                <Mail className="mr-2 h-4 w-4" />
                Export Emails
              </Button>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={filter === 'active' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('active')}
            >
              Active
            </Button>
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              All
            </Button>
            <Button
              variant={filter === 'inactive' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('inactive')}
            >
              Inactive
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            </div>
          ) : subscribers.length > 0 ? (
            <div className="grid gap-3">
              {subscribers.map((subscriber) => (
                <Card key={subscriber.id} className="p-4 bg-[hsl(var(--surface-2))] border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Mail className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="font-ui text-base">{subscriber.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Subscribed: {formatDate(subscriber.subscribedAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={subscriber.active ? 'default' : 'secondary'}>
                        {subscriber.active ? 'Active' : 'Inactive'}
                      </Badge>
                      {subscriber.active && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleUnsubscribe(subscriber.id)}
                        >
                          <UserX className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground text-lg mb-2">No subscribers yet</p>
              <p className="text-sm text-muted-foreground">
                Newsletter signups will appear here
              </p>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
